import React, { ReactNode } from 'react';

import { FormattedMessage, IntlShape, MessageDescriptor } from 'react-intl';

import { domFormatter } from '@nhl/formatter-utilities';
import {
    BaseStyle,
    CoreUtils,
    ReactTableEnums,
    ReactTableTypes,
    ReactTableUtils,
    SortableTableHeader,
} from '@nhl/nhl-breakaway-ui';
import {
    AccessorFn,
    CellContext,
    ColumnDef,
    ColumnDefTemplate,
    createColumnHelper,
    DeepKeys,
    Row,
} from '@tanstack/react-table';

import CommonMessages from '~/core/commonMessages';
import { Alignments, PlayerStatAbbrev, PlayerStatNames } from '~/core/enums';
import { getSize } from '~/core/tableColumns/utils';
import { parseValueFromLocalizedAttribute } from '~/core/utils';
import { addSortableHeaderTitle } from '~/views/components/reactTable/utils';

import { RankAccessor, ClinchIndicator as ClinchIndicatorEnum } from '../../enums';
import StandingsMessages from '../../messages';
import { StandingsSeason, TeamStandingsRecord } from '../../types';

import { GamesRecordType } from './enums';
import { getPointPercentage, getRecordStringForType, getShootoutRecord, getTeamSiteUrlFromCommonName } from './utils';

import { CenteredTableHeader, ClinchIndicator, StyledTeamDisplay, TeamAndClinchContainer } from './styles';

const columnHelper = createColumnHelper<TeamStandingsRecord>();

const renderNonSortedColumnHeader = (header: MessageDescriptor): ReactNode => (
    <CenteredTableHeader>
        <CoreUtils.SafeFormattedMessage message={header} />
    </CenteredTableHeader>
);

type Options = {
    cell?: ColumnDefTemplate<CellContext<TeamStandingsRecord, unknown>>;
    sortDescFirst?: boolean;
    size?: number;
    sortingFn?: (obj1: Row<TeamStandingsRecord>, obj2: Row<TeamStandingsRecord>, prop: string) => number;
    meta?: ReactTableTypes.MetaType;
};

const createStandingsCol = (
    intl: IntlShape,
    acc: AccessorFn<TeamStandingsRecord> | DeepKeys<TeamStandingsRecord>,
    headerString: MessageDescriptor,
    options: Options = {}
) =>
    columnHelper.accessor(acc, {
        header: ({ sortedState }: ReactTableTypes.HeaderContextType<TeamStandingsRecord, unknown>) => (
            <SortableTableHeader
                sortIndex={sortedState?.index}
                sortDesc={sortedState?.desc}
                alignment={Alignments.center}
            >
                <CoreUtils.SafeFormattedMessage message={headerString} />
            </SortableTableHeader>
        ),
        cell: options.cell === undefined ? (cell) => cell.getValue() : options.cell,
        size: getSize(intl, headerString, options.size === undefined ? 36 : options.size),
        sortingFn: options.sortingFn || 'auto',
        meta: options.meta,
        sortDescFirst: options.sortDescFirst,
    });

const getStandingsColumns = (
    rankAccessor: RankAccessor,
    currentSeason: StandingsSeason,
    isSmallScreen: boolean,
    intl: IntlShape
): Array<ColumnDef<TeamStandingsRecord>> =>
    [
        createStandingsCol(intl, rankAccessor, CommonMessages.tableHeaders.long.rank, {
            size: 48,
            cell: ({ getValue }) =>
                getValue() === undefined ? (
                    <FormattedMessage {...CommonMessages.statsDescriptions.symbols.noInfo} />
                ) : (
                    getValue()
                ),
            sortingFn: ReactTableUtils.sortUndefinedAtBottom(),
            meta: {
                ...addSortableHeaderTitle(CommonMessages.tableHeaders.long.rank, intl),
                headerStyle: { marginLeft: '8px' },
                style: { marginLeft: '8px' },
            },
        }),
        columnHelper.display({
            id: 'teamName',
            header: () => <FormattedMessage {...StandingsMessages.table.headers.team} />,
            cell: (params) => {
                const row = params.row;
                const { seasonId, teamAbbrev, teamCommonName, teamLogo, teamName, clinchIndicator } =
                    row?.original || {};
                const parsedTeamAbbrev = parseValueFromLocalizedAttribute(teamAbbrev);
                const parsedTeamName = parseValueFromLocalizedAttribute(teamName);
                const displayedName = isSmallScreen ? parsedTeamAbbrev : parsedTeamName;
                const team = { triCode: parsedTeamAbbrev };
                const teamSiteUrl = getTeamSiteUrlFromCommonName(teamCommonName);

                const clinchDescription = clinchIndicator
                    ? CoreUtils.safeFormatMessage(
                          intl,
                          StandingsMessages.table.cells.clinchAriaDescriptions[clinchIndicator]
                      )
                    : '';

                const clinchLabel = clinchIndicator ? (
                    <ClinchIndicator aria-hidden $isEliminated={clinchIndicator === ClinchIndicatorEnum.eliminated}>
                        {CoreUtils.safeFormatMessage(
                            intl,
                            StandingsMessages.table.cells.clinchIndicators[clinchIndicator],
                            clinchIndicator
                        )}
                    </ClinchIndicator>
                ) : null;

                return (
                    <TeamAndClinchContainer $isSmall={isSmallScreen}>
                        <a
                            href={teamSiteUrl}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={parsedTeamName}
                            aria-description={clinchDescription}
                        >
                            {teamLogo ? (
                                <StyledTeamDisplay
                                    className="team-display-standings"
                                    team={team}
                                    season={seasonId}
                                    useInTable
                                    logoUrlOverride={teamLogo}
                                    displayNameOverride={displayedName}
                                />
                            ) : (
                                displayedName
                            )}
                        </a>
                        {clinchLabel}
                    </TeamAndClinchContainer>
                );
            },
            size: getSize(intl, StandingsMessages.table.headers.team, isSmallScreen ? 104 : 176),
            meta: {
                className: 'left-aligned',
                headerClassName: 'left-aligned',
                headerStyle: { minWidth: isSmallScreen ? 104 : 256, margin: '0 16px 0 12px' },
                headerTitle: intl.formatMessage(StandingsMessages.table.headers.team),
                scope: ReactTableEnums.Scopes.ROW,
                style: { minWidth: isSmallScreen ? 104 : 256, margin: '0 16px 0 12px' },
            },
        }),
        createStandingsCol(
            intl,
            'gamesPlayed',
            CommonMessages.statsDescriptions.abbreviations[PlayerStatAbbrev.gamesPlayed],
            {
                meta: {
                    ...addSortableHeaderTitle(CommonMessages.statsDescriptions.long[PlayerStatNames.gamesPlayed], intl),
                },
            }
        ),
        createStandingsCol(intl, 'wins', CommonMessages.statsDescriptions.abbreviations[PlayerStatAbbrev.wins], {
            meta: {
                ...addSortableHeaderTitle(CommonMessages.statsDescriptions.long[PlayerStatNames.wins], intl),
            },
        }),
        createStandingsCol(intl, 'losses', CommonMessages.statsDescriptions.abbreviations[PlayerStatAbbrev.losses], {
            sortDescFirst: false,
            meta: {
                ...addSortableHeaderTitle(CommonMessages.statsDescriptions.long[PlayerStatNames.losses], intl),
            },
        }),
        createStandingsCol(intl, 'ties', CommonMessages.statsDescriptions.abbreviations[PlayerStatAbbrev.ties], {
            meta: {
                ...addSortableHeaderTitle(CommonMessages.statsDescriptions.long[PlayerStatNames.ties], intl),
                hidden: !currentSeason.tiesInUse,
            },
        }),
        createStandingsCol(
            intl,
            'otLosses',
            currentSeason.tiesInUse && currentSeason.pointForOTlossInUse
                ? CommonMessages.statsDescriptions.abbreviations[PlayerStatAbbrev.overtimeLosses]
                : StandingsMessages.table.headers.abbrs.overtime,
            {
                sortDescFirst: false,
                meta: {
                    ...addSortableHeaderTitle(
                        CommonMessages.statsDescriptions.long[PlayerStatNames.overtimeLosses],
                        intl
                    ),
                    hidden: !currentSeason.pointForOTlossInUse,
                },
            }
        ),
        createStandingsCol(intl, 'points', StandingsMessages.table.headers.abbrs.points, {
            size: 44,
            meta: {
                ...addSortableHeaderTitle(CommonMessages.statsDescriptions.long[PlayerStatNames.points], intl),
            },
        }),
        createStandingsCol(intl, 'pointPctg', StandingsMessages.table.headers.abbrs.pointsPercentage, {
            size: 44,
            cell: ({ row }) => getPointPercentage(row.original),
            sortingFn: ReactTableUtils.sortUndefinedAtBottom(getPointPercentage),
            meta: addSortableHeaderTitle(StandingsMessages.table.headers.long.pointsPercentage, intl),
        }),
        createStandingsCol(intl, 'regulationWins', StandingsMessages.table.headers.abbrs.regulationWins, {
            size: 44,
            meta: {
                hidden: !currentSeason.regulationWinsInUse,
                ...addSortableHeaderTitle(StandingsMessages.table.headers.long.regulationWins, intl),
            },
        }),
        createStandingsCol(intl, 'regulationPlusOtWins', StandingsMessages.table.headers.abbrs.regulationPlusOtWins, {
            size: 44,
            meta: {
                hidden: !currentSeason.rowInUse,
                ...addSortableHeaderTitle(StandingsMessages.table.headers.long.regulationPlusOtWins, intl),
            },
        }),
        createStandingsCol(intl, 'goalFor', StandingsMessages.table.headers.abbrs.goalsFor, {
            size: 44,
            meta: addSortableHeaderTitle(StandingsMessages.table.headers.long.goalsFor, intl),
        }),
        createStandingsCol(intl, 'goalAgainst', StandingsMessages.table.headers.abbrs.goalsAgainst, {
            size: 44,
            sortDescFirst: false,
            meta: addSortableHeaderTitle(CommonMessages.statsDescriptions.long[PlayerStatNames.goalsAgainst], intl),
        }),
        createStandingsCol(intl, 'goalDifferential', StandingsMessages.table.headers.abbrs.goalDifferential, {
            size: 52,
            cell: ({ getValue }) =>
                domFormatter.getPosNegCell({
                    value: getValue(),
                    posColor: BaseStyle.LightTheme.Utility.success,
                    negColor: BaseStyle.LightTheme.Utility.critical,
                }),
            meta: addSortableHeaderTitle(StandingsMessages.table.headers.long.goalDifferential, intl),
        }),
        columnHelper.display({
            id: 'home',
            header: () => renderNonSortedColumnHeader(StandingsMessages.table.headers.home),
            cell: ({ row }: { row: Row<TeamStandingsRecord> }) =>
                getRecordStringForType(row.original, currentSeason, GamesRecordType.home),
            size: getSize(intl, StandingsMessages.table.headers.home, 72),
            meta: {
                headerStyle: { textTransform: 'uppercase' },
                headerTitle: intl.formatMessage(StandingsMessages.table.headers.long.home),
            },
        }),
        columnHelper.display({
            id: 'away',
            header: () => renderNonSortedColumnHeader(StandingsMessages.table.headers.away),
            cell: ({ row }: { row: Row<TeamStandingsRecord> }) =>
                getRecordStringForType(row.original, currentSeason, GamesRecordType.road),
            size: getSize(intl, StandingsMessages.table.headers.away, 72),
            meta: {
                headerStyle: { textTransform: 'uppercase' },
                headerTitle: intl.formatMessage(StandingsMessages.table.headers.long.away),
            },
        }),
        columnHelper.display({
            id: 'shootout',
            header: () => renderNonSortedColumnHeader(StandingsMessages.table.headers.abbrs.shootout),
            cell: ({ row }) => getShootoutRecord(row.original),
            size: getSize(intl, StandingsMessages.table.headers.abbrs.shootout, 48),
            meta: {
                headerTitle: intl.formatMessage(StandingsMessages.table.headers.long.shootout),
                hidden: currentSeason.tiesInUse,
            },
        }),
        columnHelper.display({
            id: 'last10Games',
            header: () => renderNonSortedColumnHeader(StandingsMessages.table.headers.abbrs.lastTen),
            cell: ({ row }) => getRecordStringForType(row.original, currentSeason, GamesRecordType.Last10Games),
            size: getSize(intl, StandingsMessages.table.headers.abbrs.lastTen, 48),
            meta: {
                headerTitle: intl.formatMessage(StandingsMessages.table.headers.long.lastTen),
            },
        }),
        columnHelper.display({
            id: 'streak',
            header: () => renderNonSortedColumnHeader(StandingsMessages.table.headers.abbrs.streak),
            cell: ({ row }) => {
                const streakCode = row.original?.streakCode;
                const streakCount = row.original?.streakCount;
                return streakCode && streakCount ? (
                    <FormattedMessage
                        {...CommonMessages.gameInformationDescriptions.abbreviations.teamsStreak}
                        values={{
                            streakCode: CoreUtils.safeFormatMessage(
                                intl,
                                CommonMessages.statsDescriptions.abbreviations[streakCode]
                            ),
                            streakCount,
                        }}
                    />
                ) : (
                    <FormattedMessage {...CommonMessages.statsDescriptions.symbols.noInfo} />
                );
            },
            size: getSize(intl, StandingsMessages.table.headers.abbrs.streak, 40) + 4,
            meta: {
                headerTitle: intl.formatMessage(StandingsMessages.table.headers.long.streak),
                headerStyle: { paddingRight: '8px' },
                style: { paddingRight: '8px' },
            },
        }),
    ].map((col) => ({
        ...col,
        sortUndefined: false,
    }));

export default getStandingsColumns;
